import type { UpdateCompanyInput } from "@dutyon/apis";
import { findOneCompany, mapClientError, queryKeys, updateCompany } from "@dutyon/apis";
import { Button, LoadingOverlay, notifications, TitleContainer } from "@dutyon/ui-kit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { CompanyForm } from "../../components/CompanyForm";

export function CompanyUpdatePage() {
  const { id: _id } = useParams();
  const id = _id as string;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: company, isFetching } = useQuery({
    queryKey: queryKeys.companies.detail(id),
    queryFn: () => findOneCompany({ id }),
    enabled: !!id,
  });

  const mutation = useMutation({
    mutationFn: (data: UpdateCompanyInput) =>
      updateCompany({
        id,
        data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.detail(id) });
      navigate(`/companies/${id}`);
    },
    onError: (error) => {
      const mapped = mapClientError(error, { fallback: "병원 정보를 수정하지 못했습니다." });
      notifications.show({
        title: "수정 실패",
        message: mapped.message,
        color: "red",
      });
    },
  });

  if (isFetching && !company) {
    return (
      <TitleContainer title="병원 정보 수정">
        <LoadingOverlay visible />
      </TitleContainer>
    );
  }

  if (!company) {
    return <Navigate to="/404" replace />;
  }

  return (
    <TitleContainer
      title="병원 정보 수정"
      rightSection={
        <Button variant="subtle" onClick={() => navigate(`/companies/${id}`)}>
          &lt; 취소
        </Button>
      }
    >
      <CompanyForm
        initialValues={company}
        onSubmit={(data) => mutation.mutate({ name: data.name, code: data.code })}
        isLoading={mutation.isPending}
      />

      <LoadingOverlay visible={isFetching} />
    </TitleContainer>
  );
}
